import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  NgZone,
  OnDestroy,
  ViewChild
} from "@angular/core";

import { ButtonModule } from "primeng/button";
import {
  ESeverity,
  IToastService,
  toastServiceInjectionToken
} from "@zaeper/angular-dashboard-view-lib";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { IStripeService } from "../../services/stripe/i-stripe.service";
import { stripeServiceInjectionToken } from "../../services/stripe/stripe-service-injection-token";
import { IBillingRestClient } from "../../rest-clients/billing/i-billing-rest.client";
import { billingRestClientInjectionToken } from "../../rest-clients/billing/billing-rest-client-injection-token";
import type { Stripe, StripeElements, StripePaymentElement } from "@stripe/stripe-js";

export interface IAddPaymentMethodModalData {
  companyId: string;
  onSaveCallback: () => void;
  onCancelCallback?: () => void;
}

@Component({
  selector: "app-add-payment-method-modal",
  templateUrl: "./add-payment-method-modal.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    ButtonModule
]
})
export class AddPaymentMethodModalComponent implements AfterViewInit, OnDestroy {
  @ViewChild("paymentElement") paymentElementRef!: ElementRef<HTMLDivElement>;

  public isLoading: boolean = true;
  public isSubmitting: boolean = false;
  public errorMessage: string | null = null;

  private _stripe: Stripe | null = null;
  private _elements: StripeElements | null = null;
  private _paymentElement: StripePaymentElement | null = null;
  private _destroyed: boolean = false;
  private readonly _data: IAddPaymentMethodModalData;

  constructor(
    @Inject(stripeServiceInjectionToken) private readonly _stripeService: IStripeService,
    @Inject(billingRestClientInjectionToken) private readonly _billingRestClient: IBillingRestClient,
    @Inject(toastServiceInjectionToken) private readonly _toastService: IToastService,
    private readonly _dynamicDialogRef: DynamicDialogRef,
    private readonly _dynamicDialogConfig: DynamicDialogConfig,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _ngZone: NgZone
  ) {
    this._data = this._dynamicDialogConfig.data;
  }

  public ngAfterViewInit(): void {
    this._initPaymentElement();
  }

  public ngOnDestroy(): void {
    this._destroyed = true;
    this._paymentElement?.destroy();
  }

  public async submit(): Promise<void> {
    if (!this._stripe || !this._elements || this.isSubmitting) return;

    this.isSubmitting = true;
    this.errorMessage = null;
    this._safeDetectChanges();

    try {
      const { error } = await this._stripe.confirmSetup({
        elements: this._elements,
        confirmParams: {
          return_url: window.location.href
        },
        redirect: "if_required"
      });

      if (error) {
        this.errorMessage = error.message ?? "Ein Fehler ist aufgetreten";
        this.isSubmitting = false;
        this._safeDetectChanges();
      } else {
        this._toastService.setMessage({
          title: "Erfolgreich",
          detail: "Zahlungsmethode wurde hinzugefügt",
          severity: ESeverity.success
        });
        this._data.onSaveCallback();
      }
    } catch (error) {
      console.error("Failed to confirm setup:", error);
      this.errorMessage = "Die Zahlungsmethode konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.";
      this.isSubmitting = false;
      this._safeDetectChanges();
    }
  }

  public close(): void {
    this._data.onCancelCallback?.();
    this._dynamicDialogRef.close();
  }

  private async _initPaymentElement(): Promise<void> {
    try {
      this._stripe = await this._stripeService.getStripe();
    } catch (error) {
      console.error("Failed to load Stripe:", error);
      this._stripe = null;
    }

    if (this._destroyed) return;

    if (!this._stripe) {
      this.errorMessage = "Stripe konnte nicht geladen werden. Bitte überprüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.";
      this.isLoading = false;
      this._safeDetectChanges();
      return;
    }

    try {
      const setupIntent = await this._billingRestClient.createSetupIntent(this._data.companyId);

      if (this._destroyed) return;

      if (!setupIntent?.clientSecret) {
        throw new Error("SetupIntent clientSecret is missing");
      }

      const isDarkMode = document.documentElement.classList.contains("app-dark");

      this._elements = this._stripe.elements({
        clientSecret: setupIntent.clientSecret,
        appearance: {
          theme: isDarkMode ? "night" : "flat",
          variables: {
            fontFamily: "inherit",
            borderRadius: "8px",
            colorBackground: isDarkMode ? "#1e1e2e" : "#ffffff",
            colorText: isDarkMode ? "#e0e0e0" : "#1a1a1a"
          }
        }
      });

      this._paymentElement = this._elements.create("payment", {
        layout: "tabs"
      });

      this._paymentElement.on("ready", () => {
        this._ngZone.run(() => {
          this.isLoading = false;
          this._safeDetectChanges();
        });
      });

      this._paymentElement.mount(this.paymentElementRef.nativeElement);
    } catch (error: unknown) {
      if (this._destroyed) return;

      const httpError = error as { status?: number; error?: { message?: string } };

      if (httpError?.status === 0) {
        this.errorMessage = "Der Abrechnungsservice ist derzeit nicht erreichbar. Bitte versuchen Sie es später erneut.";
      } else if (httpError?.error?.message) {
        this.errorMessage = httpError.error.message;
      } else {
        this.errorMessage = "Zahlungsformular konnte nicht geladen werden. Bitte versuchen Sie es später erneut.";
      }

      console.error("Failed to initialize payment element:", error);
      this.isLoading = false;
      this._safeDetectChanges();
    }
  }

  private _safeDetectChanges(): void {
    if (!this._destroyed) {
      this._cdr.detectChanges();
    }
  }
}
